import { useMemo } from 'react';
import {
  AccommodationPlaceRoomBlockStatus,
  AccommodationPlaceType,
  EventAccommodationPublicDataFragment,
  EventAccommodationsCheckQuery,
  EventPageVisibility,
  EventPlaceType,
  TravelItemIdsFragment,
  useEventAccommodationsCheckQuery,
} from '@graphql/generated';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { sortByOrderList } from '@shared/utils/array';
import { useFeatureValue } from '@shared/core/featureFlags';

const CUSTOM_ACCOMMODATIONS_PAGE_SLUG = 'accommodations';
const TRAVEL_EVENT_PAGE_SLUG = 'travel';
const ACCOMMODATION_TRAVEL_TYPE_SET = new Set<TravelItemIdsFragment['__typename']>(['Hotel', 'RentalHome', 'Tent']);

type UseEventAccommodationsCheckArgs = {
  eventHandle: string;
};

export type UseEventAccommodationsCheckReturn = {
  /**
   * Has the query for event data resolved.
   */
  isResolved: boolean;

  eventLocationPlaceId: Maybe<string>;
  eventLocationCityName: Maybe<string>;
  eventDisplayName: Maybe<string>;
  eventFinalizedDate: Maybe<{ milliseconds: number; timezone: string }>;

  ////////////////////////////////////////////////////////////
  // Accommodations page related data
  ////////////////////////////////////////////////////////////
  accommodationListings: EventAccommodationData[];
  /**
   * Whether the event has accommodation listings (eventByName.accommodationPlacesV2).
   */
  hasAccommodationListings: boolean;
  /**
   * Whether the event has a visible accommodations event page.
   *
   * - **visible** = the page is either public or password protected.
   */
  hasVisibleAccommodationsEventPage: boolean;

  ////////////////////////////////////////////////////////////
  // Travel page related data
  ////////////////////////////////////////////////////////////

  /**
   * Whether the event has travel accommodation listings (eventByName.info.travel).
   *
   * - **visible** = the page is either public or password protected.
   * - an accommodation travel listing is a travel item that is either a Hotel, RentalHome, or Tent.
   */
  hasTravelAccommodationListings: boolean;
  /**
   * Whether the event has a visible travel event page.
   */
  hasVisibleTravelEventPage: boolean;

  error: Error | undefined;
  /**
   * The `pageSlug` of the first public custom page that matches known
   * accommodation-related keywords (e.g., "travel", "hotel", "accommodation").
   */
  matchedCustomPageSlug?: string;
};

const isEventPageVisible = (pageInfo?: { visibility: EventPageVisibility | 'hidden' | 'passwordProtected' | 'public' }) => {
  if (!pageInfo) return false;
  const visibility = pageInfo.visibility;
  return visibility === 'public' || visibility === 'passwordProtected';
};

const isAnAccommodationLikeTravelListing = (travelListing: NonNullable<EventAccommodationsCheckQuery['eventByName']>['info']['travel'][number]) => {
  return travelListing.__typename ? ACCOMMODATION_TRAVEL_TYPE_SET.has(travelListing.__typename) : false;
};

export enum AccommodationPlaceTypeExtended {
  custom = 'custom',
  hotel = 'hotel',
  roomBlock = 'roomBlock',
}
export interface ExtendedEventAccommodationPublicData extends Omit<EventAccommodationPublicDataFragment, 'type'> {
  type: AccommodationPlaceTypeExtended;
  distanceFromVenue?: number | null;
}
export type EventAccommodationData = ExtendedEventAccommodationPublicData;

export const useEventAccommodationsCheck = (args: UseEventAccommodationsCheckArgs): UseEventAccommodationsCheckReturn => {
  const { eventHandle } = args;
  const { data, loading, error } = useEventAccommodationsCheckQuery({
    variables: {
      name: eventHandle,
      pageContainerBySlugPayload: {
        eventName: eventHandle,
        pageSlug: 'accommodations',
      },
    },
    batchMode: 'off',
    fetchPolicy: 'cache-first',
  });
  const processedAccommodationsData = useMemo(() => {
    if (!data?.eventByName) return { accommodationListings: [], hasAccommodationListings: false, hasVisibleAccommodationsEventPage: false };

    const { eventByName, pageContainerBySlug } = data;
    const { places = [], accommodationPlacesV2 = [], info, pagesInfo = [] } = eventByName;

    const venue = places.find(place => place.type === EventPlaceType.venue) || info?.locationInfo;
    const accommodations = accommodationPlacesV2.filter(({ accommodationRoomBlock }) => accommodationRoomBlock?.status !== AccommodationPlaceRoomBlockStatus.cancelled);

    const travelMapComponent = pageContainerBySlug?.components?.find(comp => comp.__typename === 'TravelMapComponent');
    const ordering = travelMapComponent?.__typename === 'TravelMapComponent' ? travelMapComponent?.blockOrdering : [];
    const sortedAccommodations = ordering?.length ? sortByOrderList(accommodations, ordering) : accommodations;

    const pageInfo = pagesInfo?.find(p => p.page.pageSlug === CUSTOM_ACCOMMODATIONS_PAGE_SLUG);

    const accommodationListings = sortedAccommodations.map<EventAccommodationData>(accommodation => {
      let type = accommodation.type === AccommodationPlaceType.hotel ? AccommodationPlaceTypeExtended.hotel : AccommodationPlaceTypeExtended.custom;
      if (accommodation.accommodationRoomBlock?.isJoyManagedRoomBlock) {
        type = AccommodationPlaceTypeExtended.roomBlock;
      }
      const accommodationExtended = { ...accommodation, type };
      const distance = distanceInMiles(venue, accommodation);

      const distanceFromVenue = distance;

      return {
        ...accommodationExtended,
        distanceFromVenue,
      };
    });

    return {
      accommodationListings,
      hasAccommodationListings: accommodationListings.length > 0,
      hasVisibleAccommodationsEventPage: isEventPageVisible(pageInfo),
    };
  }, [data]);

  const processedTravelData = useMemo(() => {
    const hasTravelAccommodationListings = (data?.eventByName?.info.travel?.filter(isAnAccommodationLikeTravelListing).length ?? 0) > 0;
    const travelEventPageInfo = data?.eventByName?.pagesInfo?.find(pageInfo => pageInfo.page.pageSlug === TRAVEL_EVENT_PAGE_SLUG);

    return {
      hasTravelAccommodationListings,
      hasVisibleTravelEventPage: isEventPageVisible(travelEventPageInfo),
    };
  }, [data]);
  const eventInfo = data?.eventByName?.info;

  // Get list of all accommodations related keywords from amplitude, find first visible matching page slug from custom pages.
  const { loading: loadingSearchKeywords, payload } = useFeatureValue('accommodationsCustomPageSlugKeywords', {
    skip: processedAccommodationsData.hasVisibleAccommodationsEventPage || (processedTravelData.hasVisibleTravelEventPage && processedTravelData.hasTravelAccommodationListings),
  });
  const accommodationsCustomPageSlugKeywords = payload as string[] | null;
  // Assume config keywords are lowercased already
  const normalizedKeywords = useMemo(() => {
    return (accommodationsCustomPageSlugKeywords ?? [])
      .map(k => {
        if (k.startsWith('/') && k.endsWith('/')) {
          try {
            const pattern = k.slice(1, -1);
            return new RegExp(pattern);
          } catch (e) {
            return null;
          }
        }
        return k;
      })
      .filter(Boolean);
  }, [accommodationsCustomPageSlugKeywords]);
  // Find matching custom page slug
  const matchedCustomPageSlug = useMemo(() => {
    const matchedCustomPage = data?.eventByName?.pagesInfo?.find(pageInfo => {
      // Skip already checked accommodations and travel pages
      if (pageInfo.page.pageSlug === TRAVEL_EVENT_PAGE_SLUG) return false;
      // Skip if page is not visible
      if (!isEventPageVisible(pageInfo)) return false;
      const slug = pageInfo.page.pageSlug.toLowerCase();

      return normalizedKeywords.some(keyword => {
        if (typeof keyword === 'string') {
          return slug.includes(keyword);
        } else if (keyword instanceof RegExp) {
          return keyword.test(slug);
        }
        return false;
      });
    });

    return matchedCustomPage?.page?.pageSlug;
  }, [data?.eventByName?.pagesInfo, normalizedKeywords]);

  return {
    isResolved: !(loading || loadingSearchKeywords),
    eventDisplayName: eventInfo?.eventDisplayName,
    eventLocationPlaceId: eventInfo?.placeId,
    eventFinalizedDate: eventInfo?.finalizedDate,
    eventLocationCityName: eventInfo?.locationInfo?.city,
    error,
    ...processedAccommodationsData,
    ...processedTravelData,
    matchedCustomPageSlug,
  };
};
